import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Modal.css era carregado pelo antigo modal "Adicionado ao carrinho" e define .modal-overlay,
// usado por outros modais do admin. Continua importado aqui, na mesma posição, para não mudar o admin.
import "../css/Modal.css";
import GenericModal from "./GenericModal.js";
import "../css/Home.css";
import { useCarrinho } from "../context/CarrinhoContext";
import { useProdutos } from "../context/ProdutosContext";
import { useRestaurante } from "../context/RestauranteContext";
import { useUser } from "../context/UserContext";
import imageDefault from "../assets/no-image-default.png";
import { useFiltro } from "../context/FiltroContext";
import { useLoading } from "../context/LoadingContext";
import { apiPublica, foiCancelado } from "../services/api";
import { itensDaResposta, filtrarProdutos, montarSecoes } from "../utils/cardapio";
import { FaPlus, FaUtensils, FaWifi } from "react-icons/fa";
import { Botao, Dialogo, EstadoVazio, ListaSkeleton, Preco, SkeletonProduto } from "./ui";
import ProdutoFolha from "./ProdutoFolha";
import BarraCarrinho from "./BarraCarrinho";

// A API continua paginada (página começa em 1): busca a primeira e, se houver mais,
// as demais em paralelo, para nenhum produto ficar de fora da rolagem e da busca.
const TAMANHO_PAGINA = 500;
const MAX_PAGINAS = 20;
const carregarCardapioCompleto = async (restauranteId, signal) => {
  const primeira = await apiPublica.cardapio(restauranteId, { page: 1, size: TAMANHO_PAGINA }, signal);
  const totalPaginas = Math.min(Number(primeira?.[0]?.totalPaginas) || 1, MAX_PAGINAS);
  if (totalPaginas <= 1) return primeira || [];
  const demais = await Promise.all(
    Array.from({ length: totalPaginas - 1 }, (_, i) =>
      apiPublica.cardapio(restauranteId, { page: i + 2, size: TAMANHO_PAGINA }, signal)
    )
  );
  return [...(primeira || []), ...demais.flat()];
};

// Faixa logo abaixo da barra de categorias: a seção que passa por ela é a "atual".
const FAIXA_SECAO_ATUAL = "-80px 0px -65% 0px";

const Categorias = () => {
  const [modalExcluir, setModalExcluir] = useState(false);
  const [produtoAtual, setProdutoAtual] = useState(null);
  // Produto aberto na folha (detalhes + adicionar) e item esperando confirmar troca de restaurante.
  const [produtoFolha, setProdutoFolha] = useState(null);
  const [trocaPendente, setTrocaPendente] = useState(null);
  const { setProdutoEdit, inativarProduto } = useProdutos();
  const [tentativa, setTentativa] = useState(0);

  // Cardápio inteiro de uma vez (uma rolagem só, sem paginação), agrupado em seções por categoria.
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [erroCardapio, setErroCardapio] = useState(false);
  // Carregamento do cardápio é local (skeleton na grade); o spinner global fica para ações como excluir.
  const [carregando, setCarregando] = useState(true);

  const { carrinho, adicionarItem, restauranteCarrinho, limparCarrinho } = useCarrinho();
  const { setRestauranteId, restauranteId } = useRestaurante();
  const navigate = useNavigate();
  const { user } = useUser();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { nomeBuscaProduto } = useFiltro();

  // Barra de categorias: mostra setas/degradê quando há categorias escondidas para os lados.
  const barraRef = useRef(null);
  const [rolagem, setRolagem] = useState({ esquerda: false, direita: false });
  const atualizarRolagem = useCallback(() => {
    const el = barraRef.current;
    if (!el) return;
    setRolagem({
      esquerda: el.scrollLeft > 4,
      direita: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);
  const rolarCategorias = (direcao) => {
    barraRef.current?.scrollBy({ left: direcao * 300, behavior: "smooth" });
  };

  useEffect(() => {
    // Cancela a carga anterior ao trocar de restaurante ou sair da tela.
    const controller = new AbortController();
    const carregar = async () => {
      setCarregando(true);
      setErroCardapio(false);
      try {
        const [blocos, cats] = await Promise.all([
          carregarCardapioCompleto(id, controller.signal),
          apiPublica.categorias(id, controller.signal).catch((e) => {
            if (foiCancelado(e, controller.signal)) throw e;
            return null; // sem categorias o cardápio ainda abre, tudo em "Outros"
          }),
        ]);
        if (controller.signal.aborted) return;
        setProdutos(itensDaResposta(blocos));
        setCategorias(cats?.content || []);
        setRestauranteId(id);
      } catch (error) {
        if (foiCancelado(error, controller.signal)) return;
        console.error("Erro ao carregar o cardápio:", error);
        setErroCardapio(true);
      } finally {
        if (!controller.signal.aborted) setCarregando(false);
      }
    };
    carregar();
    return () => controller.abort();
  }, [id, tentativa]); // eslint-disable-line react-hooks/exhaustive-deps

  const secoes = useMemo(
    () => montarSecoes(filtrarProdutos(produtos, nomeBuscaProduto), categorias),
    [produtos, categorias, nomeBuscaProduto]
  );

  useEffect(() => {
    atualizarRolagem();
    window.addEventListener("resize", atualizarRolagem);
    return () => window.removeEventListener("resize", atualizarRolagem);
  }, [atualizarRolagem, secoes]);

  // Categoria em destaque acompanha a rolagem; tocar numa categoria rola até a seção dela.
  const [secaoAtiva, setSecaoAtiva] = useState(null);
  const secoesRef = useRef({});
  const chipsRef = useRef({});
  // Enquanto a rolagem suave de um clique acontece, o observador não troca o destaque
  // (senão o chip "pula" pelas seções do caminho).
  const rolandoAte = useRef(null);
  const timerRolagem = useRef(null);

  useEffect(() => {
    if (!secoes.length) return undefined;
    setSecaoAtiva((atual) => (secoes.some((s) => s.id === atual) ? atual : secoes[0].id));
    const visiveis = new Map();
    const observer = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => visiveis.set(e.target.dataset.secao, e.isIntersecting));
        if (rolandoAte.current) return;
        const primeira = secoes.find((s) => visiveis.get(s.id));
        if (primeira) setSecaoAtiva(primeira.id);
      },
      { rootMargin: FAIXA_SECAO_ATUAL }
    );
    secoes.forEach((s) => secoesRef.current[s.id] && observer.observe(secoesRef.current[s.id]));
    return () => observer.disconnect();
  }, [secoes]);

  useEffect(() => () => clearTimeout(timerRolagem.current), []);

  // Mantém o chip ativo à vista na barra (sem mexer na rolagem da página).
  useEffect(() => {
    const barra = barraRef.current;
    const chip = chipsRef.current[secaoAtiva];
    if (!barra || !chip) return;
    barra.scrollTo({ left: chip.offsetLeft - barra.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
  }, [secaoAtiva]);

  const irParaSecao = (secaoId) => {
    const el = secoesRef.current[secaoId];
    if (!el) return;
    setSecaoAtiva(secaoId);
    rolandoAte.current = secaoId;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    clearTimeout(timerRolagem.current);
    timerRolagem.current = setTimeout(() => {
      rolandoAte.current = null;
    }, 900);
  };

  const abrirProduto = (produto) => setProdutoFolha(produto);

  const primeiraImagem = (produto) => produto?.imagem?.[0] || imageDefault;

  // Um pedido vai para um restaurante só: com itens de outro restaurante (ou de restaurante
  // desconhecido) no carrinho, pergunta antes de descartar.
  const handleAdicionar = (produto, opcoes) => {
    if (carrinho.length > 0 && (!restauranteCarrinho || String(restauranteCarrinho) !== String(id))) {
      setTrocaPendente({ produto, opcoes });
      return;
    }
    adicionarItem(produto, id, opcoes);
    setProdutoFolha(null);
  };

  const cancelarTroca = () => {
    setTrocaPendente(null);
    setProdutoFolha(null);
  };

  const confirmarTroca = () => {
    const { produto, opcoes } = trocaPendente;
    limparCarrinho();
    adicionarItem(produto, id, opcoes);
    setTrocaPendente(null);
    setProdutoFolha(null);
  };

  const handleEditarProduto = (produto) => {
    setProdutoEdit(produto);
    navigate(`/restaurantes/${id}/produtos/editar`);
  };

  const handleExcluirProduto = (produto) => {
    setProdutoAtual(produto);
    setModalExcluir(true);
  };

  const confirmarExclusao = async () => {
    setIsLoading(true);
    if (produtoAtual) {
      try {
        await inativarProduto(produtoAtual.id, restauranteId);
        setModalExcluir(false);
        window.location.reload();
      } catch (error) {
        console.error("Erro ao inativar produto:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fecharModal = () => {
    setModalExcluir(false);
    setProdutoAtual(null);
  };

  const renderProduto = (produto) => (
    <div key={produto.id} className="cardapio-item">
      <div
        className="cardapio-detalhes"
        role="button"
        tabIndex={0}
        aria-label={`Ver detalhes de ${produto.nome}`}
        onClick={() => abrirProduto(produto)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            abrirProduto(produto);
          }
        }}
      >
        <img src={primeiraImagem(produto)} alt={produto.nome} loading="lazy" decoding="async" width="200" height="200" />
        <h3>{produto.nome}</h3>
        {produto.receita && <p className="cardapio-descricao">{produto.receita}</p>}
        <p className="cardapio-preco"><Preco valor={produto.preco} /></p>
      </div>
      {!user ? (
        <button
          type="button"
          className="btn-adicionar"
          aria-label={`Adicionar ${produto.nome}`}
          onClick={() => abrirProduto(produto)}
        >
          <FaPlus aria-hidden="true" />
          <span>Adicionar</span>
        </button>
      ) : (
        <div>
          <button onClick={() => handleEditarProduto(produto)}>Editar</button>
          <button onClick={() => handleExcluirProduto(produto, id)}>Deletar</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="categorias">
      {secoes.length > 0 && (
        <div className={`categorias-barra ${rolagem.esquerda ? "tem-esquerda" : ""} ${rolagem.direita ? "tem-direita" : ""}`}>
          {rolagem.esquerda && (
            <button className="categorias-seta esquerda" aria-label="Ver categorias anteriores" onClick={() => rolarCategorias(-1)}>‹</button>
          )}
          <nav className="categorias-header" ref={barraRef} onScroll={atualizarRolagem} aria-label="Categorias do cardápio">
            {secoes.map((secao) => (
              <button
                key={secao.id}
                ref={(el) => (chipsRef.current[secao.id] = el)}
                className={`categoria-button ${secaoAtiva === secao.id ? "active" : ""}`}
                aria-current={secaoAtiva === secao.id ? "true" : undefined}
                onClick={() => irParaSecao(secao.id)}
              >
                {secao.nome}
              </button>
            ))}
          </nav>
          {rolagem.direita && (
            <button className="categorias-seta direita" aria-label="Ver mais categorias" onClick={() => rolarCategorias(1)}>›</button>
          )}
        </div>
      )}

      {carregando ? (
        <ListaSkeleton className="cardapio-container" quantidade={8} componente={SkeletonProduto} />
      ) : erroCardapio ? (
        <div className="cardapio-container">
          <div className="cardapio-aviso">
            <EstadoVazio
              icone={<FaWifi />}
              titulo="Não foi possível carregar o cardápio"
              texto="Verifique sua conexão e tente de novo."
              acao={<Botao onClick={() => setTentativa((t) => t + 1)}>Tentar de novo</Botao>}
            />
          </div>
        </div>
      ) : secoes.length === 0 ? (
        <div className="cardapio-container">
          <div className="cardapio-aviso">
            <EstadoVazio
              icone={<FaUtensils />}
              titulo={nomeBuscaProduto ? "Nenhum produto encontrado" : "Cardápio vazio"}
              texto={nomeBuscaProduto ? "Confira a grafia ou busque por outro nome." : "Este restaurante ainda não cadastrou produtos."}
            />
          </div>
        </div>
      ) : (
        <div className="cardapio-secoes">
          {secoes.map((secao) => (
            <section
              key={secao.id}
              className="cardapio-secao"
              data-secao={secao.id}
              ref={(el) => (secoesRef.current[secao.id] = el)}
              aria-labelledby={`secao-${secao.id}`}
            >
              <h2 id={`secao-${secao.id}`} className="cardapio-secao__titulo">
                {secao.nome}
                <span className="cardapio-secao__qtd">{secao.produtos.length}</span>
              </h2>
              <div className="cardapio-container">{secao.produtos.map(renderProduto)}</div>
            </section>
          ))}
        </div>
      )}

      <ProdutoFolha
        produto={produtoFolha}
        aberto={Boolean(produtoFolha) && !trocaPendente}
        aoFechar={() => setProdutoFolha(null)}
        aoAdicionar={handleAdicionar}
        somenteLeitura={Boolean(user)}
      />

      <Dialogo
        aberto={Boolean(trocaPendente)}
        aoFechar={cancelarTroca}
        titulo="Começar um pedido aqui?"
        rodape={
          <div className="troca-restaurante__botoes">
            <Botao variante="secundario" tamanho="lg" largo onClick={cancelarTroca}>
              Manter carrinho
            </Botao>
            <Botao tamanho="lg" largo onClick={confirmarTroca}>
              Limpar e adicionar
            </Botao>
          </div>
        }
      >
        <p className="troca-restaurante__texto">
          Seu carrinho tem itens de outro restaurante. Cada pedido vai para um restaurante só, então os itens atuais serão removidos.
        </p>
      </Dialogo>

      {!user && <BarraCarrinho />}

      <GenericModal isOpen={modalExcluir} onClose={fecharModal}>
        <div>
          <h3 className="modal-title">Tem certeza que deseja deletar o produto?</h3>
          <div className="modal-buttons">
            <button className="modal-title-button" onClick={confirmarExclusao}>
              Deletar
            </button>
            <button className="modal-title-button" onClick={fecharModal}>
              Cancelar
            </button>
          </div>
        </div>
      </GenericModal>
    </div>
  );
};

export default Categorias;
